import {
  useAeropayUser,
  useConfirmJaneGoldUser,
  useCreateAeropayUser,
} from '@iheartjane/data-access/react-query';
import type { MoreInfoFormData } from '@iheartjane/menu';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { getJwt } from '@jane/shared/auth';
import { useJaneUser, useUpdateJaneUser } from '@jane/shared/data-access';

export const SCREEN_NAMES = {
  aeropay: 'aeropay',
  failure: 'failure',
  loading: 'loading',
  mfa: 'mfa',
  moreInfo: 'more-info',
  success: 'success',
  successConfirmation: 'success-confirmation',
  signedInWelcome: 'signed-in-welcome',
};

type ScreenName = (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES];

export const useLinkBankOnDeviceData = ({
  initialScreen,
  onSetNewPhone,
  setError,
}: {
  initialScreen?: (typeof SCREEN_NAMES)[keyof typeof SCREEN_NAMES] | null;
  onSetNewPhone: (phone: string | undefined) => void;
  setError: (err: unknown) => void;
}) => {
  const [, setQueryParams] = useSearchParams();
  const [screen, _setScreen] = useState<ScreenName>(
    initialScreen || SCREEN_NAMES.signedInWelcome
  );
  const [loaded, setLoaded] = useState(false);
  const [needsMoreFlag, setNeedsMoreFlag] = useState(false);
  // Store new user info until after MFA
  const [pendingUserInfo, setPendingUserInfo] = useState<{
    first_name?: string;
    last_name?: string;
    phone?: string;
  } | null>(null);
  const setScreen = (newScreen: ScreenName) => {
    setQueryParams({ step: newScreen });
    _setScreen(newScreen);
  };

  const {
    data: janeUser,
    isFetched: janeUserFetched,
    error: userError,
  } = useJaneUser();
  const userId = janeUser?.user?.id;

  const jwt = getJwt();
  const { data: aeropayUser, error: aeropayUserError } = useAeropayUser({
    janeUserId: userId || null,
    jwt,
  });

  const { mutate: updateJaneUser } = useUpdateJaneUser();

  const { mutate: createAeropayUser } = useCreateAeropayUser({
    janeUserId: userId!,
    jwt,
  });

  const { mutate: confirmJaneGoldUser } = useConfirmJaneGoldUser({
    janeUserId: userId!,
    jwt,
  });

  // NOTE: this is used for only the jane gold success screen
  const handleDoneWithOptOut = async (optOut?: boolean) => {
    if (optOut !== undefined) {
      updateJaneUser({
        first_name: janeUser?.user?.first_name,
        promotions_opt_out: optOut,
      });

      confirmJaneGoldUser();

      setScreen(SCREEN_NAMES.successConfirmation);
    }
  };

  const handleSubmitInfo = useCallback(
    (data: MoreInfoFormData) => {
      setScreen(SCREEN_NAMES.loading);
      const { firstName: first_name, lastName: last_name, phone } = data;

      setPendingUserInfo({ first_name, last_name, phone });
      onSetNewPhone(phone);

      if (!aeropayUser?.user_id) {
        createAeropayUser(
          {
            email: janeUser?.user?.email,
            first_name,
            last_name,
            phone,
          },
          {
            onSuccess: () => {
              setScreen(SCREEN_NAMES.aeropay);
            },
            onError: (e) => {
              console.error(`Create Aeropay User Error: ${e}`);
              setError(e);
              setScreen(SCREEN_NAMES.failure);
            },
          }
        );
      } else {
        setScreen(SCREEN_NAMES.aeropay);
      }
    },
    [aeropayUser, screen]
  );

  const afterVerifySuccess = useCallback(() => {
    if (!pendingUserInfo) return;

    updateJaneUser(pendingUserInfo, {
      onSuccess: () => setPendingUserInfo(null),
      onError: () => {
        setError('updateJaneUser');
        setScreen(SCREEN_NAMES.failure);
      },
    });
  }, [pendingUserInfo]);

  useEffect(() => {
    if (janeUser?.user?.id && aeropayUser && !loaded) {
      const needsMoreInfo =
        !janeUser?.user?.first_name ||
        !janeUser?.user.last_name ||
        !janeUser?.user?.phone;

      // If they do not have an aeropay account yet, we make one,
      // or request more info to do so
      if (!aeropayUser.user_id) {
        if (needsMoreInfo) {
          setScreen(SCREEN_NAMES.moreInfo);
        } else {
          createAeropayUser(
            {
              email: janeUser.user.email,
              first_name: janeUser.user.first_name,
              last_name: janeUser.user.last_name,
              phone: janeUser.user.phone,
            },
            {
              onSuccess: () => {
                setScreen(SCREEN_NAMES.aeropay);
              },
              onError: (e) => {
                setError(e);
                setScreen(SCREEN_NAMES.failure);
              },
            }
          );
        }
      } else if (needsMoreInfo) {
        setScreen(SCREEN_NAMES.moreInfo);
      } else {
        setScreen(SCREEN_NAMES.aeropay);
      }
      setLoaded(true);
      setNeedsMoreFlag(needsMoreInfo);
    }
  }, [aeropayUser, createAeropayUser, loaded, janeUser, janeUserFetched]);

  useEffect(() => {
    if (userError || aeropayUserError) {
      setError(userError ? 'fetchJaneUser' : aeropayUserError);
      setScreen(SCREEN_NAMES.failure);
    }
  }, [userError, aeropayUserError]);

  return {
    afterVerifySuccess,
    handleDoneWithOptOut,
    handleSubmitInfo,
    needsMoreFlag,
    pendingPhone: pendingUserInfo?.phone,
    screen,
    setScreen,
  };
};
